<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <TabsComponent
        v-model="activeTab"
        :tabs="tabs"
        class="mb-4"
      />

      <div v-if="activeTab === 'edit-date'" class="flex flex-col w-full">
        <div class="flex flex-col gap-2 mb-3">
          <FullCalendar
            v-model="newDate"
            :min-date="today"
            mode="compact"
            class="w-full"
          />
        </div>

        <div class="flex flex-col gap-2 my-6">
          <span class="font-medium">בחירת שעה</span>
          <settings-toggle
            class="!px-2"
            :status="showOutOfWorkingHours"
            title="הצגת שעות מחוץ לשעות הפעילות"
            @value-changed="showOutOfWorkingHours = !showOutOfWorkingHours" />

          <span v-if="!newDate">יש לבחור תאריך</span>
          <template v-else-if="availableSlots.length">
            <div class="flex flex-wrap w-full gap-2 mt-2 justify-start items-start">
              <div
                v-for="slot in visibleSlots"
                :key="slot.value"
                @click="selectHour(slot)"
                :class="[
                  'rounded-lg py-2 px-3 border text-center w-[calc(100%/4-6px)] md:cursor-pointer',
                  selectedHour?.value === slot.value ? 'border-black border-2' : ''
                ]">
                <span>{{ slot.name.replace('- admin', '') }}</span>
              </div>
              <span v-if="visibleSlots.length === 0">אין תורים פנויים</span>
            </div>
          </template>

          <div v-else-if="!availableSlotsWasLoaded"
               class="flex flex-wrap w-full gap-2 justify-start items-start">
            <div v-for="i in [1,2,3,4,5,6]" :key="i">
              <Skeleton borderRadius="8px" width="5.5rem" height="2.5rem" />
            </div>
          </div>
          <span v-else>אין תורים פנויים</span>

          <!-- Manual start time — the owner's override. Unlike the slots above it is
               applied exactly as typed, even over an existing visit or outside the
               working hours. -->
          <div class="flex flex-col gap-2 mt-4 pt-4 border-t">
            <span class="text-sm font-medium">שעה מותאמת אישית</span>
            <div class="flex gap-2 items-center">
              <InputText
                v-model="customHourInput"
                size="small"
                class="flex-1"
                placeholder="לדוגמה 08:43"
                maxlength="5"
                @keyup.enter="applyCustomHour" />
              <Button label="הוספה" size="small" @click="applyCustomHour" />
            </div>
            <small v-if="customHourError" class="text-red-500">{{ customHourError }}</small>
            <div
              v-if="hourWasTyped"
              class="flex items-start gap-2 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2">
              <i class="pi pi-bolt text-primary text-sm mt-0.5" />
              <span class="text-xs text-primary-700">
                השעה {{ selectedHour?.value }} תיקבע בדיוק כפי שהוזנה — גם אם היא חופפת לתור אחר או מחוץ לשעות הפעילות.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="activeTab === 'edit-duration'" class="flex flex-col w-full">
        <div v-if="steps?.length" class="flex flex-col gap-3 mb-3">
          <span class="font-medium mb-1">אורך שלבי הביקור</span>
          <div v-for="(step, idx) of steps" :key="step.stepIndex" class="flex flex-col gap-1">
            <span class="text-sm text-gray-600">{{ step.description || `שלב ${idx + 1}` }}</span>
            <div class="w-full relative">
              <InputNumber
                v-model="stepDurations[idx]"
                :max="500"
                :min="1"
                class="mb-1 w-full" />
              <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">דקות</span>
            </div>
          </div>
          <div class="text-sm text-gray-500">
            סה״כ אורך ביקור: {{ totalStepsDuration }} דקות
          </div>
        </div>
        <div v-else class="flex flex-col gap-2 mb-3">
          <span class="font-medium mb-1">אורך ביקור</span>
          <div class="w-full relative">
            <InputNumber
              v-model="selectedDuration"
              :max="500"
              placeholder="לדוגמא, 120"
              :min="1"
              class="mb-2 w-full" />
            <span class="bg-white absolute left-[0.75rem] top-[0.625rem]">דקות</span>
          </div>
        </div>
      </div>

      <div v-else-if="activeTab === 'edit-services'" class="flex flex-col w-full gap-3">
        <div v-if="selectedServices.length" class="flex flex-col gap-2 p-3 rounded-lg bg-gray-50">
          <span class="font-medium">שירותים שנבחרו ({{ selectedServices.length }})</span>
          <ul class="text-sm pe-5 list-none">
            <li v-for="service in selectedServices" :key="service.id">
              {{ service.name }}
              <span class="text-gray-500">({{ resolveServiceDuration(service) }} דקות)</span>
            </li>
          </ul>
          <div class="text-sm text-gray-500">
            סה״כ אורך ביקור מעודכן: {{ newServicesDuration }} דקות
          </div>
          <div v-if="originalServicesDuration && newServicesDuration !== originalServicesDuration"
               class="text-xs text-gray-500">
            (השינוי: {{ originalServicesDuration }} ← {{ newServicesDuration }} דקות)
          </div>
        </div>
        <div v-else class="text-sm text-gray-500">
          בחרו שירות אחד או יותר עבור התור
        </div>

        <span class="font-medium mb-1 mt-2">בחירת שירותים</span>
        <div v-if="!availableServices.length" class="flex flex-col justify-center items-center py-4">
          <span>אין שירותים</span>
        </div>
        <div v-else class="flex flex-col">
          <div v-for="(service, index) in availableServices"
               :key="service.id"
               @click="toggleService(service)"
               :class="['w-full py-4 flex gap-3 justify-start items-center border-gray-200 cursor-pointer', index !== availableServices.length - 1 ? 'border-b' : '']">
            <Avatar
              v-if="service.imageUrl"
              size="large"
              :image="service.imageUrl"
              class="rounded-2xl overflow-hidden flex-none"/>
            <div class="flex flex-grow justify-between items-center gap-2">
              <div class="flex flex-col gap-1 items-start justify-start">
                <span class="font-medium">{{ service.name }}</span>
                <span class="text-sm">{{ service.description }}</span>
              </div>
              <i :class="['pi', isServiceSelected(service.id) ? 'pi-circle-fill' : 'pi-circle']" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <drawer-footer
      class="flex-shrink-0"
      :approveDisable="disableButton"
      :approveLoading="loading"
      approveLabel="עדכון תור"
      cancelLabel="חזור"
      @approve-click="saveAppointment"
      @cancel-click="$emit('close')"
    />

    <RecurringEventActionModal
      v-model:visible="showRecurringModal"
      action-type="edit"
      :event-title="appointmentTitle"
      @action="handleRecurringAction" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { IAppointmentStep, IDropdownItem, IService } from '@torim-app/types';
import { DateTime } from 'luxon';
import { AppointmentsApi } from '@app/api/admin';
import { getStringHour, resolveServiceDuration, totalServicesDuration } from '@torim-app/utils';
import { useToast } from 'vue-toastification';
import Skeleton from 'primevue/skeleton';
import { RecurringEventsApi } from '@app/api/admin/recurring-events-api';
import { RecurringActionScope } from '@app/components/admin/modals/RecurringEventActionModal.vue';
import { FullCalendar } from '@torim-app/shared-components';
import { TabsComponent } from '@torim-app/shared-components';
import { trackEvent } from '@app/composables/posthog';
import { useAdminStore } from '@app/stores';

const showRecurringModal = ref(false);

const props = defineProps({
  appointmentTitle: { type: String, required: true },
  apid: { type: String, required: true },
  appointmentDate: { type: Date, required: true },
  appointmentHour: { type: Object, required: false },
  recurringManualEventId: { type: String, required: false, default: null },
  steps: { type: Array as () => IAppointmentStep[], required: false, default: null },
  servicesIds: { type: Array as () => string[], required: false, default: () => [] }
});
const emit = defineEmits(['close', 'complete']);
const toast = useToast();

const today = new Date();
const adminStore = useAdminStore();
const tabs = [
  { label: 'שינוי תאריך', value: 'edit-date' },
  { label: 'שינוי אורך ביקור', value: 'edit-duration' },
  { label: 'שינוי שירותים', value: 'edit-services' }
];
const availableSlotsWasLoaded = ref<boolean>(false);
const loading = ref<boolean>(false);
const newDate = ref<Date>(props.appointmentDate);
const selectedHour = ref<IDropdownItem>();
const selectedDuration = ref<number>();
const stepDurations = ref<number[]>(
  props.steps?.map(step => Math.round((step.timeTo - step.timeFrom) * 60)) ?? []
);
const totalStepsDuration = computed(() => stepDurations.value.reduce((sum, d) => sum + (d || 0), 0));
const availableSlots = ref<Array<IDropdownItem>>([]);
const activeTab = ref<'edit-date' | 'edit-duration' | 'edit-services'>('edit-date');
const showOutOfWorkingHours = ref<boolean>(false);

const availableServices = computed<IService[]>(() => adminStore.getServices);
const initialServices = computed<IService[]>(() =>
  availableServices.value.filter(s => props.servicesIds?.includes(s.id))
);
const selectedServices = ref<IService[]>(initialServices.value);
const originalServicesDuration = computed(() => totalServicesDuration(initialServices.value));
const newServicesDuration = computed(() => totalServicesDuration(selectedServices.value));
const servicesChanged = computed(() => {
  const original = (props.servicesIds ?? []).slice().sort().join(',');
  const next = selectedServices.value.map(s => s.id).sort().join(',');
  return original !== next;
});

const isServiceSelected = (id: string) =>
  selectedServices.value.some(s => s.id === id);

const toggleService = (service: IService) => {
  if (isServiceSelected(service.id)) {
    selectedServices.value = selectedServices.value.filter(s => s.id !== service.id);
  } else {
    selectedServices.value = [...selectedServices.value, service];
  }
};

const visibleSlots = computed(() =>
  availableSlots.value.filter(s => showOutOfWorkingHours.value || !s.name.includes('- admin'))
);

const selectHour = (slot: IDropdownItem) => {
  selectedHour.value = slot;
  hourWasTyped.value = false;
  customHourError.value = '';
};

const customHourInput = ref<string>('');
const customHourError = ref<string>('');
// True once the owner typed a start time by hand. That is the signal to save with
// `force`, so the server places the visit there without the usual slot checks.
const hourWasTyped = ref(false);

const padTime = (num: number) => (num < 10 ? `0${num}` : `${num}`);

const applyCustomHour = () => {
  customHourError.value = '';
  const match = customHourInput.value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    customHourError.value = 'יש להזין שעה בפורמט HH:MM (לדוגמה 08:43)';
    return;
  }

  const hours = +match[1];
  const minutes = +match[2];
  if (hours > 23 || minutes > 59) {
    customHourError.value = 'שעה לא תקינה';
    return;
  }

  const stringHour = `${padTime(hours)}:${padTime(minutes)}`;
  selectedHour.value = { name: stringHour, value: stringHour, numericValue: hours + minutes / 60 };
  hourWasTyped.value = true;
};

const handleRecurringAction = async (scope: RecurringActionScope) => {
  try {
    loading.value = true;
    if (scope === 'instance') {
      await AppointmentsApi.editAppointment(props.apid,
        'select',
        newDate.value ? DateTime.fromJSDate(newDate.value).toISODate()! : undefined,
        selectedHour.value ? selectedHour.value.numericValue : undefined,
        effectiveDuration.value,
        effectiveStepDurations.value,
        hourWasTyped.value);
      toast.success('עדכנו את התור שלך 😄');
    } else {
      await RecurringEventsApi.updateEntireSeries(
        props.recurringManualEventId, // This is the recurringEventId
        {
          timeFrom: selectedHour.value?.numericValue,
          title: props.appointmentTitle,
          newDate: DateTime.fromJSDate(newDate.value).toISODate(),
          referenceDate: DateTime.fromJSDate(props.appointmentDate).toISODate()
        }
      );
      toast.success('עדכנו את כל האירועים העתידיים 😄');
    }
    showRecurringModal.value = false;
    setTimeout(() => emit('complete'), 500);
  } catch (e) {
    console.error(e);
    toast.error('הפעולה נכשלה, אנא נסו שוב');
  } finally {
    loading.value = false;
  }
};

// Only meaningful on the duration tab: the server treats a duration as a duration-ONLY
// update and ignores the date/time alongside it, so sending it from the date tab would
// silently drop the reschedule (it did, for appointments with steps).
const effectiveDuration = computed(() => {
  if (activeTab.value !== 'edit-duration') return undefined;
  if (props.steps?.length) {
    return totalStepsDuration.value;
  }
  return selectedDuration.value;
});

const effectiveStepDurations = computed(() => {
  if (activeTab.value !== 'edit-duration') return undefined;
  if (props.steps?.length) {
    return stepDurations.value;
  }
  return undefined;
});

const saveAppointment = () => {
  if (disableButton.value) return;

  if (activeTab.value === 'edit-services') {
    trackEvent('appointment_services_edited');
    loading.value = true;
    AppointmentsApi.updateAppointmentServices(props.apid, selectedServices.value.map(s => s.id))
      .then(() => {
        toast.success('עדכנו את שירותי הביקור 😄');
        emit('complete');
      })
      .catch(err => {
        console.log(err);
        toast.error('לא הצלחנו לעדכן את שירותי הביקור');
      })
      .finally(() => {
        loading.value = false;
      });
    return;
  }

  trackEvent('appointment_time_edited');

  if (props.recurringManualEventId) {
    showRecurringModal.value = true;
  } else {
    loading.value = true;
    AppointmentsApi.editAppointment(props.apid, 'select', newDate.value ? DateTime.fromJSDate(newDate.value).toISODate()! : undefined, selectedHour.value ? selectedHour.value.numericValue : undefined, effectiveDuration.value, effectiveStepDurations.value, hourWasTyped.value)
      .then(() => {
        toast.success('עדכנו את הביקור שלך 😄');
        emit('complete');
      })
      .catch(err => {
        console.log(err);
        if (err === 'Slot is taken')
          toast.error('נראה שהתור הזה נתפס');
        else
          toast.error('לא הצלחנו לעדכן את התור');
      });
  }
};

const disableButton = computed(() => {
  if (activeTab.value === 'edit-date') {
    return !newDate.value || !selectedHour.value || !selectedHour.value.numericValue;
  }
  if (activeTab.value === 'edit-duration') {
    if (props.steps?.length) {
      return stepDurations.value.some(d => !d || d < 1);
    }
    return !selectedDuration.value;
  }
  if (activeTab.value === 'edit-services') {
    return !selectedServices.value.length || !servicesChanged.value;
  }
  return true;
});

const fetchAvailableSlots = async () => {
  if (!newDate.value) return;

  availableSlots.value.length = 0;
  availableSlotsWasLoaded.value = false;
  const selectedDate = DateTime.fromJSDate(newDate.value).toISODate();
  const slots = await AppointmentsApi.getEventAlternativeSlots(props.apid, selectedDate as string);

  availableSlots.value.push(...[
    ...slots.available.map(x => ({
      name: getStringHour(x),
      value: getStringHour(x),
      numericValue: x
    })),
    ...slots.adminAvailable.map(x => ({
      name: `${getStringHour(x)} - admin`,
      value: getStringHour(x),
      numericValue: x
    }))].sort((a, b) => a.numericValue > b.numericValue ? 1 : -1));
  availableSlotsWasLoaded.value = true;
};

watch(() => newDate.value, async () => {
  if (newDate.value)
    await fetchAvailableSlots();
}, { deep: true });

onMounted(() => {
  fetchAvailableSlots();
});

</script>

<style scoped lang="scss">

</style>
